// ── OrgRenewalChainGroup — ORA.9 ORA list-page renewal chain grouping ───────────
//
// Renders a parent ORA entry followed by its renewal children (indented + labelled).
// Threshold: ≤3 renewals fully expanded; ≥4 collapsed behind "Show all N renewals".
//
// D-ORA-ARCH-9 / MI ORA.9 scope: PARALLEL component (org-domain only).
// Vendor parallel: VendorRenewalChainGroup.tsx

import { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  RotateCcw,
  MoreHorizontal,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { RrStatusCell } from '@/components/risk-acceptances/RrStatusCell';
import { RR_TYPE_SPECS, deriveStage } from '@/components/risk-acceptances/rrPresentation';
import { ApprovedCell, ExpiresCell } from '@/components/risk-acceptances/rrDates';
import { Button } from '@/components/ui/button';

// ── RR-UX-1 S1: dates now come from the SHARED rrDates module (one treatment across vendor + org). ──
// ── RR-BI-1 Slice 1: the response-type chip (card 6) — hued chip, coloured left edge, planned/implemented
// stage riding inside as a suffix. A SEPARATE mechanism from the workflow-status badge; they never merge. ──
function TypeChip({ entry }: { entry: OraEntry }) {
  const spec = RR_TYPE_SPECS[(entry.planned_response_type ?? 'accept') as keyof typeof RR_TYPE_SPECS] ?? RR_TYPE_SPECS.accept;
  const stage = deriveStage(entry);
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded border border-border-subtle bg-bg-surface-2 py-0.5 pl-1.5 pr-2 text-[11px] font-semibold text-text-secondary"
      style={{ borderLeftColor: spec.hue, borderLeftWidth: 3 }}
    >
      <span className="font-bold" style={{ color: spec.hue }}>{spec.glyph}</span>{spec.name}
      <span className="font-normal text-text-faint">· {stage}</span>
    </span>
  );
}
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { OraEntry, OraActionType } from '@/api/types/org-risk-acceptance.types';

const COLLAPSE_THRESHOLD = 3;

interface OrgRenewalChainGroupProps {
  parent: OraEntry;
  children: OraEntry[];
  isApproverClass: boolean;
  currentUserId: string | null;
  onNavigate: (id: string) => void;
  onAction: (action: OraActionType, entry: OraEntry) => void;
}

export function OrgRenewalChainGroup({
  parent,
  children,
  isApproverClass,
  currentUserId,
  onNavigate,
  onAction,
}: OrgRenewalChainGroupProps) {
  const [expanded, setExpanded] = useState(children.length <= COLLAPSE_THRESHOLD);

  return (
    <>
      <OrgGroupRow
        entry={parent}
        isChild={false}
        isChainParent={children.length > 0}
        renewalIndex={undefined}
        isApproverClass={isApproverClass}
        currentUserId={currentUserId}
        onNavigate={onNavigate}
        onAction={onAction}
      />

      {children.length > COLLAPSE_THRESHOLD && (
        <div className="px-4 py-1.5 border-b border-border-subtle bg-bg-surface-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs text-text-secondary gap-1"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3 w-3" />
                Collapse renewals
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" />
                Show all {children.length} renewals
              </>
            )}
          </Button>
        </div>
      )}

      {expanded && children.map((child, idx) => (
        <OrgGroupRow
          key={child.id}
          entry={child}
          isChild
          isChainParent={false}
          renewalIndex={idx + 1}
          isLastChild={idx === children.length - 1}
          isApproverClass={isApproverClass}
          currentUserId={currentUserId}
          onNavigate={onNavigate}
          onAction={onAction}
        />
      ))}
    </>
  );
}

// ── OrgOrphanChildRow ─────────────────────────────────────────────────────────
//
// Flat standalone row for a renewal child whose parent is absent from the
// current page (e.g. filtered out or on a different page).

interface OrgOrphanChildRowProps {
  entry: OraEntry;
  isApproverClass: boolean;
  currentUserId: string | null;
  onNavigate: (id: string) => void;
  onAction: (action: OraActionType, entry: OraEntry) => void;
}

export function OrgOrphanChildRow(props: OrgOrphanChildRowProps) {
  return (
    <OrgGroupRow
      entry={props.entry}
      isChild
      isChainParent={false}
      renewalIndex={undefined}
      isLastChild
      isApproverClass={props.isApproverClass}
      currentUserId={props.currentUserId}
      onNavigate={props.onNavigate}
      onAction={props.onAction}
    />
  );
}

// ── OrgGroupRow (internal) ────────────────────────────────────────────────────

interface OrgGroupRowProps {
  entry: OraEntry;
  isChild: boolean;
  // ⚠ Slice 2 renewal delta (card 8): the chain root wears an "Original" badge (only when it HAS renewals).
  isChainParent: boolean;
  renewalIndex: number | undefined;
  // ⚠ Slice 2 renewal delta (card 8): the child group carries ONE accent left-edge; round its foot on the last.
  isLastChild?: boolean;
  isApproverClass: boolean;
  currentUserId: string | null;
  onNavigate: (id: string) => void;
  onAction: (action: OraActionType, entry: OraEntry) => void;
}

function OrgGroupRow({
  entry,
  isChild,
  isChainParent,
  renewalIndex,
  isLastChild,
  isApproverClass,
  currentUserId,
  onNavigate,
  onAction,
}: OrgGroupRowProps) {
  const showActionMenu =
    isApproverClass && (
      entry.workflow_status === 'pending_approval' ||
      entry.workflow_status === 'approved'
    );

  const isRenewalChild = entry.parent_acceptance_id !== null;

  return (
    <div
      // ⚠ Slice 2 date-column polish (MI): the fix for the empty band is the DATE twins — Approved/Expires are
      // FIXED 7rem tracks, right-anchored (everything to their right is fixed) so they align vertically across
      // these independent per-row grids. Status/Type stay `auto` (a long "Closed — …" phrase never clips — the
      // minmax(0,1fr) Title yields and truncates instead of the old ballooned 1fr). Cell SEMANTICS are FROZEN.
      // ⚠ Slice 2 renewal delta (card 8, mirrors VendorRenewalChainGroup): the chain reads as ONE block joined
      // by a continuous accent left-edge — stronger on the Original, lighter on the children.
      className={`grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_auto_7rem_7rem_2.5rem] gap-x-4 gap-y-1 px-4 py-3 border-b border-border-subtle last:border-0 items-center hover:bg-bg-surface-2 transition-colors ${
        isChild
          ? `bg-bg-surface-2/40 border-l-2 border-accent-cyan/30 ${isLastChild ? 'rounded-bl' : ''}`
          : isChainParent ? 'border-l-2 border-accent-cyan/50' : ''
      }`}
    >
      {/* Title — chain-root "Original" badge (card 8), or a "Renewal N" prefix + indent for children */}
      <button
        className={`text-left text-sm font-medium text-text-primary hover:text-accent-cyan flex items-center gap-1.5 min-w-0 ${isChild ? 'pl-6' : ''}`}
        onClick={() => onNavigate(entry.id)}
      >
        {isChild ? (
          <span className="text-text-secondary text-xs font-normal shrink-0">
            ↳ {renewalIndex != null ? `Renewal ${renewalIndex}` : 'Renewal'}
          </span>
        ) : isChainParent ? (
          <span className="shrink-0 rounded border border-accent-cyan/40 bg-accent-cyan/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent-cyan">
            Original
          </span>
        ) : null}
        <span className="truncate">{entry.title ?? '—'}</span>
      </button>

      {/* Status (workflow_status badge — card 6) */}
      <div className="flex items-center">
        <RrStatusCell entry={entry} />
      </div>

      {/* Response type (hued chip + stage — card 6; a separate dimension from status) */}
      <div className="flex items-center">
        <TypeChip entry={entry} />
      </div>

      {/* Approved — the actual date, a first-class token (card 4: never relative) */}
      <ApprovedCell iso={entry.approved_at} />

      {/* Expires — actual date + urgency pill; "No expiry" for non-Accept org items with none (card 4) */}
      <ExpiresCell iso={entry.acceptance_expires_at} />

      {/* Action menu */}
      <div className="flex justify-end w-8">
        {showActionMenu ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {entry.workflow_status === 'pending_approval' ? (
                isRenewalChild ? (
                  <>
                    <DropdownMenuItem
                      disabled={currentUserId === entry.submitted_by_user_id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (currentUserId !== entry.submitted_by_user_id) {
                          onAction('approve-renewal', entry);
                        }
                      }}
                    >
                      <CheckCircle className="mr-2 h-3.5 w-3.5 text-accent-emerald" />
                      Approve Renewal
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); onAction('deny-renewal', entry); }}
                      className="text-accent-rose"
                    >
                      <XCircle className="mr-2 h-3.5 w-3.5" />
                      Deny Renewal
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem
                      disabled={currentUserId === entry.submitted_by_user_id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (currentUserId !== entry.submitted_by_user_id) {
                          onAction('approve', entry);
                        }
                      }}
                    >
                      <CheckCircle className="mr-2 h-3.5 w-3.5 text-accent-emerald" />
                      Approve
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); onAction('deny', entry); }}
                      className="text-accent-rose"
                    >
                      <XCircle className="mr-2 h-3.5 w-3.5" />
                      Deny
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); onAction('return', entry); }}
                    >
                      <RotateCcw className="mr-2 h-3.5 w-3.5" />
                      Return for Info
                    </DropdownMenuItem>
                  </>
                )
              ) : (
                <>
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onAction('close-as-mitigated', entry); }}
                  >
                    <CheckCircle className="mr-2 h-3.5 w-3.5 text-accent-emerald" />
                    Close as Mitigated
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={currentUserId === entry.submitted_by_user_id}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentUserId !== entry.submitted_by_user_id) {
                        onAction('revoke', entry);
                      }
                    }}
                    className="text-accent-rose"
                  >
                    <XCircle className="mr-2 h-3.5 w-3.5" />
                    Revoke
                  </DropdownMenuItem>
                  {entry.renewal_window_open && (
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); onNavigate(entry.id); }}
                    >
                      <RefreshCw className="mr-2 h-3.5 w-3.5 text-accent-cyan" />
                      Request Renewal…
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}
